import { MessengerService } from 'src/app/core/service/messenger.service';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, Subscription } from 'rxjs';
import { EmailTemplateList, EmailTemplateEdit } from 'src/app/email-template/model/email-template';
import { TranslateService } from '@ngx-translate/core';
import { Cacheable, CacheBuster } from 'ts-cacheable';

const emailTemplateCacheBuster = new Subject<void>();
@Injectable({
  providedIn: 'root'
})
export class EmailTemplateService implements OnDestroy
{
  // Email Template Types - EMAIL-TEMPLATE-TYPES
  public templateTypes: Array<{ text: string; value: string }> = [
    { text: 'User Registration', value: 'Registration' },
    { text: 'User Forget Password', value: 'ResetPassword' },
    { text: 'Appointment Scheduled', value: 'AppointmentScheduled' },
    { text: 'Appointment Cancelled', value: 'AppointmentCancelled' },
    { text: 'Appointment Rescheduled', value: 'AppointmentRescheduled' },
    { text: 'Appointment Confirmation', value: 'AppointmentConfirmation' },
    { text: 'Consent Confirmation', value: 'ConsentConfirmation' },
    { text: 'Community Registration', value: 'CommunityRegistration' },
    { text: 'Community Password Recovery', value: 'CommunityPasswordRecovery' },
    { text: 'Community Username Recovery', value: 'CommunityUsernameRecovery' },
    { text: 'Message Received', value: 'MessageReceived' },
    { text: 'Provider Referral', value: 'ProviderReferral' },
    { text: 'Community Invite', value: 'CommunityInvite' },
    { text: 'User Referral', value: 'UserReferral' },
    { text: 'Member Referral', value: 'MemberReferral' },
    { text: 'SMS Appointment Confirmation', value: 'SMSAppointmentConfirmation' },
    { text: 'SMS Consent Confirmation', value: 'SMSConsentConfirmation' },
    { text: 'SMS Community Invite', value: 'SMSCommunityInvite' },
    { text: 'Password Updated', value: 'PasswordUpdated' },
    { text: 'Community Username Updated', value: 'CommunityUsernameUpdated' },
    { text: 'Community Password Updated', value: 'CommunityPasswordUpdated' },
    { text: 'SMS User Referral', value: 'SMSUserReferral' },
    { text: 'SMS Member Referral', value: 'SMSMemberReferral' },
    { text: 'Image Capture Link', value: 'ImageCaptureLink' },
    { text: 'SMS Image Capture Link', value: 'SMSImageCaptureLink' },
    { text: 'Two Step Verification', value: 'AuthTwoStep' },
    { text: 'SMS Two Step Verification', value: 'SMSAuthTwoStep' },
    { text: 'Community Service Correction', value: 'CommunityServiceCorrection' },
    { text: 'Community Service Batch Corrections', value: 'CommunityServiceBatchCorrections' },
    { text: 'Consent Request Link', value: 'ConsentRequestLink' },
    { text: 'SMS Consent Request Link', value: 'SMSConsentRequestLink' },
    { text: 'Forgot Username', value: 'ForgotUsername' },
    { text: 'Opt Out', value: 'OptOut' },
    { text: 'SMS Opt Out', value: 'SMSOptOut' },
  ];

  public templateMimeTypes: Array<{ text: string; value: string }> = [
    { text: 'HTML', value: 'HTML' },
    { text: 'Text', value: 'TEXT' }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Delete an email template.
   *
   * @param id Email template id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: emailTemplateCacheBuster
    })
  public deleteEmailTemplate(id: number): Observable<EmailTemplateEdit>
  {
    return this.http.delete<EmailTemplateEdit>(`/email/template/${id}`);
  }

  /**
   * Get Email Templates.
   */
  @Cacheable(
    {
      cacheBusterObserver: emailTemplateCacheBuster
    })
  public getCachedEmailTemplatesList(templateType: string = null): Observable<EmailTemplateList[]>
  {
    let queryString = '';
    if (templateType !== null)
    {
      queryString = `?type=${templateType}`;
    }
    return this.http.get<EmailTemplateList[]>(`/email/templates/list${queryString}`);
  }

  /**
   * Save an Email Template.
   *
   * @param data Email Template to persit
   */
  @CacheBuster(
    {
      cacheBusterNotifier: emailTemplateCacheBuster
    })
  public saveEmailTemplate(data: any, isNew: boolean, emailTemplateId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post(`/email/template`, data);
    }
    else
    {
      return this.http.put(`/email/template/${emailTemplateId}`, data);
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get the Email Template Type text to display.
   *
   * @param type Email Template Type value.
   */
  public getEmailTemplateTypeText(type: string): string
  {
    let text = type;
    const typeObject = this.templateTypes.find(x => x.value === type);
    if (typeObject !== undefined)
    {
      text = typeObject.text;
    }
    return text;
  }

  /**
   * Get the Email Template Mime Type text to display.
   *
   * @param type Email Template Mime Type value.
   */
  public getEmailTemplateMimeTypeText(type: string): string
  {
    let text = type;
    const typeObject = this.templateMimeTypes.find(x => x.value === type);
    if (typeObject !== undefined)
    {
      text = typeObject.text;
    }
    return text;
  }

  /**
   * Get Email Templates.
   */
  public getEmailTemplatesList(templateType: string = null): Observable<EmailTemplateList[]>
  {
    let queryString = '';
    if (templateType !== null)
    {
      queryString = `?type=${templateType}`;
    }
    return this.http.get<EmailTemplateList[]>(`/email/templates/list${queryString}`);
  }

  /**
   * Get an Email Template by id.
   *
   * @param id Email Template id
   */
  public getEmailTemplateById(id: number): Observable<EmailTemplateEdit>
  {
    return this.http.get<EmailTemplateEdit>(`/email/template/${id}`);
  }

  /**
   * Check if email template is in use.
   *
   * @param id Email template ID
   */
  public emailTemplateInUse(id: number): Observable<boolean>
  {
    return this.http.get<boolean>(`/email/template/inuse/${id}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    setTimeout(() =>
    {
      this.translate.get(['EMAIL-TEMPLATE-TYPES', 'EMAIL-TEMPLATE-MIME-TYPES']).subscribe({
        next: (res: string) =>
        {
          let splitted = res['EMAIL-TEMPLATE-TYPES'].split(',');
          for (let i = 0; i < splitted.length; i++)
          {
            if (this.templateTypes.length > i)
            {
              this.templateTypes[i].text = splitted[i];
            }
          }

          splitted = res['EMAIL-TEMPLATE-MIME-TYPES'].split(',');
          for (let i = 0; i < splitted.length; i++)
          {
            if (this.templateMimeTypes.length > i)
            {
              this.templateMimeTypes[i].text = splitted[i];
            }
          }
        }
      });
    });
  }
}
